import React from 'react';
import { motion } from 'framer-motion';
import {
  FiFileText,
  FiBriefcase,
  FiHome,
  FiHeart,
  FiUsers,
  FiShield,
  FiTruck,
  FiDollarSign,
  FiGlobe,
  FiBookOpen,
} from 'react-icons/fi';

const Especialidades = () => {
  const especialidades = [
    {
      id: 1,
      name: 'Derecho Civil',
      description:
        'Contratos, responsabilidad civil, daños y perjuicios, derecho de propiedad y obligaciones.',
      icon: FiFileText,
      color: 'bg-blue-500',
    },
    {
      id: 2,
      name: 'Derecho Laboral',
      description:
        'Relaciones laborales, despidos, acoso laboral, contratos de trabajo y seguridad social.',
      icon: FiBriefcase,
      color: 'bg-green-500',
    },
    {
      id: 3,
      name: 'Derecho Inmobiliario',
      description:
        'Compraventa de propiedades, arrendamientos, hipotecas y registro de bienes raíces.',
      icon: FiHome,
      color: 'bg-purple-500',
    },
    {
      id: 4,
      name: 'Derecho Familiar',
      description:
        'Divorcios, custodia de menores, pensiones alimenticias, adopciones y sucesiones.',
      icon: FiHeart,
      color: 'bg-pink-500',
    },
    {
      id: 5,
      name: 'Derecho Corporativo',
      description:
        'Constitución de empresas, fusiones, adquisiciones, compliance y gobierno corporativo.',
      icon: FiUsers,
      color: 'bg-indigo-500',
    },
    {
      id: 6,
      name: 'Derecho Penal',
      description:
        'Defensa penal, delitos económicos, violencia doméstica y procedimientos penales.',
      icon: FiShield,
      color: 'bg-red-500',
    },
    {
      id: 7,
      name: 'Derecho de Tránsito',
      description:
        'Accidentes de tráfico, infracciones viales, seguros de automóviles y responsabilidad civil.',
      icon: FiTruck,
      color: 'bg-yellow-500',
    },
    {
      id: 8,
      name: 'Derecho Fiscal',
      description:
        'Asesoría tributaria, controversias fiscales, planeación fiscal y cumplimiento de obligaciones.',
      icon: FiDollarSign,
      color: 'bg-emerald-500',
    },
    {
      id: 9,
      name: 'Derecho Internacional',
      description:
        'Comercio internacional, migración, tratados internacionales y arbitraje comercial.',
      icon: FiGlobe,
      color: 'bg-cyan-500',
    },
    {
      id: 10,
      name: 'Propiedad Intelectual',
      description:
        'Marcas, patentes, derechos de autor, secretos industriales y protección de datos.',
      icon: FiBookOpen,
      color: 'bg-orange-500',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1,
      },
    },
  };

  const headerVariants = {
    hidden: { opacity: 0, y: -50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.25, 0.1, 0.25, 1],
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 50, scale: 0.9 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: [0.25, 0.1, 0.25, 1],
      },
    },
    hover: {
      y: -8,
      scale: 1.02,
      transition: {
        duration: 0.3,
        ease: [0.25, 0.1, 0.25, 1],
      },
    },
  };

  const iconVariants = {
    hover: {
      rotate: 360,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.1, 0.25, 1],
      },
    },
  };

  return (
    <div className="min-h-screen bg-gray-50 py-10">
      {/* Header Section */}
      <motion.section
        initial="hidden"
        animate="visible"
        variants={headerVariants}
        className="bg-white py-16 px-4 sm:px-6 lg:px-8 border-b border-gray-200"
      >
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
            Especialidades Legales
          </h1>
          <p className="text-xl sm:text-2xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Conoce todas las áreas del derecho donde nuestros abogados especializados pueden
            ayudarte a resolver tus necesidades legales.
          </p>
        </div>
      </motion.section>

      {/* Especialidades Grid */}
      <motion.section
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={containerVariants}
        className="py-16 px-4 sm:px-6 lg:px-8"
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
            {especialidades.map(especialidad => {
              const IconComponent = especialidad.icon;
              return (
                <motion.div
                  key={especialidad.id}
                  variants={cardVariants}
                  whileHover="hover"
                  className="bg-white rounded-xl shadow-lg p-8 border border-gray-100 cursor-pointer"
                >
                  <div className="flex items-center mb-6">
                    <motion.div
                      variants={iconVariants}
                      whileHover="hover"
                      className={`${especialidad.color} p-3 rounded-lg mr-4`}
                    >
                      <IconComponent className="w-6 h-6 text-white" />
                    </motion.div>
                    <h3 className="text-xl font-bold text-gray-900">{especialidad.name}</h3>
                  </div>
                  <p className="text-gray-600 leading-relaxed">{especialidad.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </motion.section>

      {/* Call to Action */}
      <motion.section
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={headerVariants}
        className="bg-blue-600 py-16 px-4 sm:px-6 lg:px-8 my-6"
      >
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            ¿Ya sabes qué especialidad necesitas?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Encuentra al abogado especialista perfecto para tu caso específico.
          </p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="bg-white text-blue-600 hover:bg-gray-50 font-semibold py-4 px-8 rounded-lg shadow-lg transition-colors duration-200 text-lg"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            Buscar Abogado
          </motion.button>
        </div>
      </motion.section>
    </div>
  );
};

export default Especialidades;
